// Root-only console: UI text is hardcoded English by design, do not add i18n keys here.
import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { toast } from "react-toastify";
import { FaTrash, FaPen } from "react-icons/fa6";
import { Spinner } from "../components/atoms/Spinner";
import { CustomButton } from "../components/atoms/CustomButton";
import SegmentedTabs from "../components/molecules/SegmentedTabs";
import { SideDrawer } from "../components/atoms/SideDrawer";
import ActionButton from "../components/atoms/ActionButton";
import ConfirmModal from "../components/public/ConfirmModal";
import WhatsAppMessagePreview, {
  resolvePreviewButtons,
} from "../components/pages/dashboard/providers/WhatsAppMessagePreview";
import LibraryTemplateEditor, {
  EditorTemplate,
} from "../components/pages/dashboard/providers/LibraryTemplateEditor";
import useAuth from "../hooks/useAuth";
import {
  useGetWhatsappLibrary,
  useDeleteWhatsappLibraryTemplate,
} from "../hooks/providers";
import TwilioAccountPanel from "../components/pages/dashboard/providers/TwilioAccountPanel";
import TwilioMessagesPanel from "../components/pages/dashboard/providers/TwilioMessagesPanel";
import BrevoPanel from "../components/pages/dashboard/providers/BrevoPanel";
import KmmQueuePanel from "../components/pages/dashboard/providers/KmmQueuePanel";

type ProviderStatus = "active" | "soon";
interface ProviderEntry {
  id: string;
  name: string;
  blurb: string;
  status: ProviderStatus;
}

const PROVIDERS: ProviderEntry[] = [
  {
    id: "twilio",
    name: "Twilio",
    blurb: "WhatsApp templates, account health and message delivery.",
    status: "active",
  },
  {
    id: "brevo",
    name: "Brevo",
    blurb: "Transactional email: credits, sending stats, domains.",
    status: "active",
  },
  {
    id: "kmm",
    name: "KMM",
    blurb: "Message queue: pending, scheduled, failed; retry and cancel.",
    status: "active",
  },
];

interface LibraryButton {
  type: string;
  payment?: boolean;
  title: string | Record<string, string>;
}
interface LibraryTemplate {
  key: string;
  category: string;
  hasMedia: boolean;
  languages: string[];
  body: Record<string, string>;
  variables: { key: string; label: string }[];
  samples?: Record<string, string[]> | null;
  sample_image_url?: string | null;
  buttons?: LibraryButton[] | null;
}
const sampleValueFor = (
  variable: { key: string; label: string },
  samples?: string[] | null
): string => {
  const fromSamples = samples?.[Number(variable.key) - 1];
  if (fromSamples) return fromSamples;
  const label = variable.label || "";
  if (variable.key === "1") return "Marie";
  const example = label.match(/\(ex\.\s*([^)]+)\)/i);
  if (example) return example[1].trim();
  if (/lien|link/i.test(label)) return "https://exemple.com";
  return label;
};

const fillBody = (
  body: string,
  variables: { key: string; label: string }[],
  samples?: string[] | null
): string => {
  let out = body || "";
  (variables || []).forEach((v) => {
    out = out.replace(
      new RegExp(`\\{\\{\\s*${v.key}\\s*\\}\\}`, "g"),
      sampleValueFor(v, samples)
    );
  });
  return out;
};

const WhatsAppTemplatesPanel: React.FC<{
  customer: string;
  entity: string;
}> = ({ customer, entity }) => {
  const { data, isLoading, isError, refetch } = useGetWhatsappLibrary(
    customer,
    entity
  );
  const { mutate: deleteTemplate, isLoading: isDeleting } =
    useDeleteWhatsappLibraryTemplate();

  const [previewKey, setPreviewKey] = useState<string | null>(null);
  const [previewLang, setPreviewLang] = useState<string>("fr");
  const [pendingDelete, setPendingDelete] = useState<string | null>(null);
  const [editorOpen, setEditorOpen] = useState<boolean>(false);
  const [editorInitial, setEditorInitial] = useState<EditorTemplate | null>(
    null
  );

  const templates: LibraryTemplate[] = data?.templates ?? [];
  const previewTemplate = templates.find((t) => t.key === previewKey) || null;

  const handleDelete = (key: string) => {
    deleteTemplate(
      { customer_identification: customer, entity_identification: entity, key },
      {
        onSuccess: () => {
          toast.success(`Model "${key}" removed from the catalogue.`);
          setPendingDelete(null);
        },
        onError: (err: any) => {
          toast.error(err?.response?.data?.message || err.message);
        },
      }
    );
  };

  const openEditor = (tpl: LibraryTemplate | null) => {
    setEditorInitial(
      tpl
        ? {
            key: tpl.key,
            category: tpl.category,
            hasMedia: tpl.hasMedia,
            languages: tpl.languages,
            variables: tpl.variables,
            body: tpl.body,
            samples: tpl.samples ?? null,
            sample_image_url: tpl.sample_image_url ?? null,
            buttons: tpl.buttons ?? null,
          }
        : null
    );
    setEditorOpen(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-6 text-gray-500">
        <Spinner /> Loading library...
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-2 p-6">
        <p className="text-sm text-gray-700">
          The template catalogue could not be read, so nothing about it is known
          here.
        </p>
        <CustomButton
          variant="secondary"
          onClick={() => refetch()}
          title="Retry"
        />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">
        These are the model definitions SUM offers. A template is approved for
        the WhatsApp account that submits it, so each customer gets these models
        submitted on their own WABA, and their approval state lives with their
        entity, not here.
      </p>

      <div className="flex flex-wrap items-center gap-3">
        <CustomButton
          variant="secondary"
          onClick={() => openEditor(null)}
          title="New template"
        />
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-xs uppercase text-gray-500">
            <tr>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Languages</th>
              <th className="px-3 py-2">Preview (fr)</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {templates.map((tpl) => (
              <tr
                key={tpl.key}
                className="cursor-pointer border-t border-gray-100 align-top hover:bg-gray-50"
                onClick={() => {
                  setPreviewKey(tpl.key);
                  setPreviewLang(tpl.languages[0] || "fr");
                }}
              >
                <td className="px-3 py-2 font-medium text-gray-800">
                  <div className="flex items-center gap-2">
                    {tpl.sample_image_url && (
                      <img
                        src={tpl.sample_image_url}
                        alt=""
                        className="h-9 w-14 rounded object-cover"
                      />
                    )}
                    <span>
                      {tpl.key}
                      {tpl.hasMedia && (
                        <span className="ml-1 text-xs text-gray-400">
                          image
                        </span>
                      )}
                    </span>
                  </div>
                </td>
                <td className="px-3 py-2">
                  <span
                    className={`rounded-md px-2 py-0.5 text-xs ${
                      tpl.category === "marketing"
                        ? "bg-blue-100 text-blue-700"
                        : "bg-gray-100 text-gray-600"
                    }`}
                  >
                    {tpl.category}
                  </span>
                </td>
                <td className="px-3 py-2">
                  <div className="flex flex-wrap gap-1">
                    {tpl.languages.map((language) => (
                      <span
                        key={language}
                        className="rounded bg-gray-100 px-1.5 py-0.5 text-xs uppercase text-gray-600"
                      >
                        {language}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-3 py-2 text-xs text-gray-600">
                  {tpl.body?.fr}
                </td>
                <td className="px-3 py-2">
                  <div className="flex items-center gap-1">
                    <ActionButton
                      aria-label={`Edit ${tpl.key}`}
                      tooltip="Edit this model's definition"
                      title={<FaPen size={12} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditor(tpl);
                      }}
                    />
                    <ActionButton
                      colorScheme="red"
                      aria-label={`Delete ${tpl.key}`}
                      tooltip="Remove this model from the catalogue"
                      title={<FaTrash size={13} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setPendingDelete(tpl.key);
                      }}
                    />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-gray-500">
        Click a model to preview it as a WhatsApp message.
      </p>

      <SideDrawer
        open={!!previewKey}
        onClose={() => setPreviewKey(null)}
        title="Template preview"
      >
        {previewTemplate && (
          <div className="space-y-3 p-1">
            <div className="flex gap-2">
              {previewTemplate.languages.map((lang) => (
                <button
                  key={lang}
                  type="button"
                  onClick={() => setPreviewLang(lang)}
                  className={`rounded-md px-3 py-1 text-sm ${
                    previewLang === lang
                      ? "bg-blue-100 text-blue-700"
                      : "bg-gray-100 text-gray-500"
                  }`}
                >
                  {lang.toUpperCase()}
                </button>
              ))}
            </div>
            <WhatsAppMessagePreview
              imageUrl={previewTemplate.sample_image_url}
              body={fillBody(
                previewTemplate.body?.[previewLang] || "",
                previewTemplate.variables,
                previewTemplate.samples?.[previewLang]
              )}
              isMarketing={previewTemplate.category === "marketing"}
              buttons={resolvePreviewButtons(
                previewTemplate.buttons,
                previewLang
              )}
            />
            <p className="text-xs text-gray-500">
              These are the sample values submitted to Meta for approval. The
              image is the Meta approval sample; merchants supply their own
              image and values when sending.
            </p>
          </div>
        )}
      </SideDrawer>

      <ConfirmModal
        show={!!pendingDelete}
        onClose={() => setPendingDelete(null)}
        title="Remove this model from the catalogue?"
        description={
          <span>
            <strong>{pendingDelete}</strong> stops being offered to merchants
            who do not have it yet. Templates already approved on a customer's
            own WABA are not touched: they keep working, and removing them is
            done on their account.
          </span>
        }
        cancelBtnName="Cancel"
        cancelBtnFn={() => setPendingDelete(null)}
        confirmBtnName="Remove"
        confirmBtnFn={() => pendingDelete && handleDelete(pendingDelete)}
        isLoading={isDeleting}
        ConfirmBtnColor="red"
        icon={<FaTrash className="text-red-600" />}
        iconBgColor="bg-red-100"
      />

      <LibraryTemplateEditor
        show={editorOpen}
        onClose={() => setEditorOpen(false)}
        customer={customer}
        entity={entity}
        initial={editorInitial}
        onDelete={(key) => {
          setEditorOpen(false);
          setPendingDelete(key);
        }}
      />
    </div>
  );
};

type TwilioTab = "templates" | "account" | "messages";
const TWILIO_TABS: { key: TwilioTab; label: string }[] = [
  { key: "templates", label: "WhatsApp templates" },
  { key: "account", label: "Account & senders" },
  { key: "messages", label: "Recent messages" },
];

const TwilioPanel: React.FC<{ customer: string; entity: string }> = ({
  customer,
  entity,
}) => {
  const [tab, setTab] = useState<TwilioTab>("templates");
  return (
    <div className="space-y-4">
      <SegmentedTabs<TwilioTab>
        tabs={TWILIO_TABS}
        active={tab}
        onChange={setTab}
      />
      {tab === "templates" && (
        <WhatsAppTemplatesPanel customer={customer} entity={entity} />
      )}
      {tab === "account" && (
        <TwilioAccountPanel customer={customer} entity={entity} />
      )}
      {tab === "messages" && (
        <TwilioMessagesPanel customer={customer} entity={entity} />
      )}
    </div>
  );
};

const Providers: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const entity_identification = user?.entity_identification;
  const [selected, setSelected] = useState<string>("twilio");

  return (
    <div className="p-4 md:p-6">
      <Helmet>
        <title>Providers</title>
      </Helmet>

      <h1 className="mb-1 text-xl font-semibold text-gray-900">Providers</h1>
      <p className="mb-5 text-sm text-gray-500">
        Infrastructure and platform monitoring for our outbound providers.
        Twilio carries WhatsApp (via Meta) and SMS; Brevo carries email; KMM is
        the message queue in front of them.
      </p>

      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {PROVIDERS.map((provider) => {
          const isSelected = selected === provider.id;
          return (
            <button
              key={provider.id}
              type="button"
              onClick={() => setSelected(provider.id)}
              className={`rounded-lg border p-4 text-left transition hover:border-blue-400 ${
                isSelected
                  ? "border-blue-500 ring-1 ring-blue-500"
                  : "border-gray-200"
              }`}
            >
              <div className="flex items-center justify-between">
                <span className="font-semibold text-gray-800">
                  {provider.name}
                </span>
                <span className="rounded-md bg-green-100 px-2 py-0.5 text-xs text-green-700">
                  active
                </span>
              </div>
              <p className="mt-1 text-xs text-gray-500">{provider.blurb}</p>
            </button>
          );
        })}
      </div>

      {selected === "twilio" && entity_identification && (
        <TwilioPanel
          customer={customer_identification}
          entity={entity_identification}
        />
      )}
      {selected === "brevo" && entity_identification && (
        <BrevoPanel
          customer={customer_identification}
          entity={entity_identification}
        />
      )}
      {selected === "kmm" && entity_identification && (
        <KmmQueuePanel
          customer={customer_identification}
          entity={entity_identification}
        />
      )}
    </div>
  );
};

export default Providers;
